import { useEffect } from "react";
import tokens from "@/brand/tokens.json";

/**
 * Mission Dresseur — SEO + Meta layer (LLM-friendly).
 *
 * Consumes the brand registry for canonical name, tagline, og:image base.
 * Renders nothing — manipulates document head directly to support both
 * SSR-future-ready paths and current CRA flow.
 *
 * Usage:
 *   <SEO title="..." description="..." og={{ template: "mission", title, subtitle, stat1: {...} }} />
 *
 * `og` is optional: if omitted, falls back to the default OG template with
 * the page title.
 */
export default function SEO({
    title,
    description,
    canonical,
    image,                  // explicit OG image URL (overrides og.template)
    og = null,              // { template, title, subtitle, eyebrow, stat1_label, stat1_value, stat2_label, stat2_value }
    type = "website",       // og:type
    locale = tokens.brand.lang,
    noindex = false,
    schema = null,          // JSON-LD object or array
}) {
    useEffect(() => {
        const fullTitle = title ? `${title} — ${tokens.brand.name}` : `${tokens.brand.name} — ${tokens.brand.tagline}`;
        document.title = fullTitle;

        const meta = (name, content, prop = false) => {
            if (content == null) return;
            const sel = prop ? `meta[property="${name}"]` : `meta[name="${name}"]`;
            let el = document.head.querySelector(sel);
            if (!el) {
                el = document.createElement("meta");
                if (prop) el.setAttribute("property", name);
                else el.setAttribute("name", name);
                document.head.appendChild(el);
            }
            el.setAttribute("content", content);
        };

        const desc = description || tokens.brand.doctrine;
        meta("description", desc);
        meta("robots", noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large");
        meta("theme-color", tokens.core.navy.hex);

        // Canonical
        let canonicalEl = document.head.querySelector('link[rel="canonical"]');
        if (canonical) {
            if (!canonicalEl) {
                canonicalEl = document.createElement("link");
                canonicalEl.setAttribute("rel", "canonical");
                document.head.appendChild(canonicalEl);
            }
            canonicalEl.setAttribute("href", canonical);
        }

        // OpenGraph / Twitter
        const ogImage = image || buildOgImageUrl(og, title);
        meta("og:site_name", tokens.brand.name, true);
        meta("og:locale", locale, true);
        meta("og:type", type, true);
        meta("og:title", fullTitle, true);
        meta("og:description", desc, true);
        if (canonical) meta("og:url", canonical, true);
        if (ogImage) {
            meta("og:image", ogImage, true);
            meta("og:image:width", "1200", true);
            meta("og:image:height", "630", true);
        }
        meta("twitter:card", "summary_large_image");
        meta("twitter:title", fullTitle);
        meta("twitter:description", desc);
        if (ogImage) meta("twitter:image", ogImage);

        // JSON-LD structured data (LLM-friendly)
        const existingLd = document.head.querySelectorAll('script[data-md-jsonld]');
        existingLd.forEach((n) => n.remove());

        const baseLd = {
            "@context": "https://schema.org",
            "@type": "Organization",
            name: tokens.brand.name,
            slogan: tokens.brand.tagline,
            url: `https://${tokens.brand.domain}`,
            inLanguage: locale,
        };
        const all = [baseLd];
        if (schema) {
            if (Array.isArray(schema)) all.push(...schema);
            else all.push(schema);
        }
        all.forEach((obj) => {
            const s = document.createElement("script");
            s.type = "application/ld+json";
            s.setAttribute("data-md-jsonld", "true");
            s.textContent = JSON.stringify(obj);
            document.head.appendChild(s);
        });
    }, [title, description, canonical, image, JSON.stringify(og), type, locale, noindex, JSON.stringify(schema)]);

    return null;
}

function buildOgImageUrl(og, fallbackTitle) {
    if (!og) {
        const base = process.env.REACT_APP_BACKEND_URL || "";
        const t = fallbackTitle || tokens.brand.name;
        return `${base}/api/og/default?title=${encodeURIComponent(t)}`;
    }
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const params = new URLSearchParams();
    Object.entries(og).forEach(([k, v]) => {
        if (k === "template") return;
        if (v != null && v !== "") params.set(k, String(v));
    });
    return `${base}/api/og/${og.template || "default"}?${params.toString()}`;
}
